import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link2, ExternalLink, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ConnectZerodha() {
  const { id } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("PENDING");
  const [sessionStatus, setSessionStatus] = useState("CONNECTING");

  const stored = (() => {
    try { return JSON.parse(sessionStorage.getItem(`gz_auth_${id}`) || "{}"); } catch { return {}; }
  })();

  // Payload the Chrome extension content-script reads (nonce handoff, NO enctoken).
  const payload = JSON.stringify({
    auth_request_id: id, secret: stored.secret || "", backend_base: BACKEND_BASE,
  });

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get(`/zerodha/auth-request/${id}/status`);
        setStatus(data.auth_request_status);
        setSessionStatus(data.session?.status || "CONNECTING");
        if (data.session?.status === "CONNECTED") {
          toast.success("Zerodha connected");
          sessionStorage.removeItem(`gz_auth_${id}`);
          setTimeout(() => nav("/broker"), 1200);
        }
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [id, nav]);

  const connected = sessionStatus === "CONNECTED";
  const replaceRequired = status === "REJECTED_REPLACE_REQUIRED";
  const replaceBlocked = status === "REJECTED_REPLACE_BLOCKED";

  // One user = one Zerodha account: switching to a DIFFERENT client id is an explicit replace.
  const replaceLinked = async () => {
    if (!window.confirm("Replace your linked Zerodha account? The current account is unlinked from "
      + "GroundZero (its history is kept). Continue only if you are switching Zerodha accounts.")) return;
    try {
      const { data } = await api.post("/zerodha/auth-request", { replace: true });
      sessionStorage.setItem(`gz_auth_${data.auth_request_id}`,
        JSON.stringify({ auth_request_id: data.auth_request_id, secret: data.secret }));
      nav(data.connect_path);
    } catch (e) { toast.error("Could not start the replace request"); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      {/* Hidden nonce payload for the GroundZero Chrome extension content script */}
      <script id="gz-broker-auth-request" type="application/json"
        data-payload={payload} dangerouslySetInnerHTML={{ __html: payload }} />

      <button onClick={() => nav("/broker")} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Broker Session</button>

      <div className="term-panel rounded-sm p-6 space-y-4" data-testid="connect-zerodha-page">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-[#2563EB]" />
          <h1 className="text-lg font-bold tracking-tight">Connect Zerodha</h1>
          <span className={`ml-auto text-[11px] font-bold px-2.5 py-1 rounded-sm ${connected ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}
            data-testid="connect-status">
            {connected ? "ZERODHA CONNECTED" : `WAITING FOR ZERODHA · ${status}`}
          </span>
        </div>

        <ol className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="h-6 w-6 shrink-0 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] flex items-center justify-center text-xs mono">1</span>
            <div>Make sure the <b>GroundZero Chrome extension</b> is installed. This page has securely published a one-time connect request for it (no token in the URL).</div>
          </li>
          <li className="flex gap-3">
            <span className="h-6 w-6 shrink-0 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] flex items-center justify-center text-xs mono">2</span>
            <div>Log in to your Kite account.
              <a href="https://kite.zerodha.com/" target="_blank" rel="noreferrer" className="ml-2 text-[#5b9bff] inline-flex items-center gap-1">Open kite.zerodha.com <ExternalLink className="h-3 w-3" /></a>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="h-6 w-6 shrink-0 rounded-sm bg-[#1A1D24] border border-[#2A2F3A] flex items-center justify-center text-xs mono">3</span>
            <div>Open the extension and click <b>Authenticate with GroundZero</b>. It reads your Kite <span className="mono">user_id</span> + <span className="mono">enctoken</span> and sends them to the backend with the one-time secret. This page updates automatically.</div>
          </li>
        </ol>

        <div className="flex items-center gap-2 pt-2 border-t border-[#2A2F3A]">
          {connected ? <CheckCircle2 className="h-5 w-5 text-[#00C805]" /> : <Loader2 className="h-5 w-5 text-[#FFCC00] animate-spin" />}
          <span className="text-sm text-slate-300">{connected ? "Connected — redirecting…" : "Waiting for the extension to complete authentication…"}</span>
        </div>

        {replaceRequired && (
          <div className="text-[12px] text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm p-3 space-y-2" data-testid="replace-required">
            <div>You logged in to a <b>different</b> Zerodha account than the one linked to this GroundZero user. A GroundZero user has one Zerodha account.</div>
            <button onClick={replaceLinked} data-testid="replace-linked-btn"
              className="px-3 py-1.5 rounded-sm text-[11px] font-bold bg-[#FFCC00]/20 border border-[#FFCC00]/40">REPLACE LINKED ACCOUNT</button>
          </div>
        )}
        {replaceBlocked && (
          <div className="text-[12px] text-[#FF6b62] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3" data-testid="replace-blocked">
            The linked Zerodha account still has active trades. Complete them before replacing the account.
          </div>
        )}
        {!stored.secret && (
          <div className="text-[11px] text-[#FF6b62] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-2">
            This connect request was not initiated in this browser session. Return to Broker Session and press CONNECT ZERODHA again.
          </div>
        )}
        <div className="text-[10px] text-slate-600">The enctoken is verified against your Zerodha profile and stored encrypted on the server. It is never shown here or placed in any URL.</div>
      </div>
    </div>
  );
}
